import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate'

import { resolveBackendOrigin } from '../utils/api'

const backendOrigin = resolveBackendOrigin()
const API_BASE_URL = `${backendOrigin}/api`
const UPLOADS_BASE_URL = `${backendOrigin}/uploads`

let refreshTokenInFlight = null

function normalizeUploadValue(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw

  const uploadsMatch = raw.match(/\/uploads\/([^?#]+)/i)
  if (uploadsMatch?.[1]) return uploadsMatch[1]

  return raw.replace(/^\/?uploads\//i, '')
}

function resolveCompanyLogoUrlLocal(value) {
  const raw = normalizeUploadValue(value)
  if (!raw) return ''
  if (
    raw.startsWith('http://') ||
    raw.startsWith('https://') ||
    raw.startsWith('blob:') ||
    raw.startsWith('data:')
  ) {
    return raw
  }
  return `${UPLOADS_BASE_URL}/${raw}`
}

function normalizeUserLogo(user) {
  if (!user) return user

  const resolvedLogo = resolveCompanyLogoUrlLocal(
    user.companyLogo ||
    user.company_logo ||
    user.logo ||
    user.company?.logo_url ||
    user.company?.logoUrl
  )

  const company = user.company
    ? {
        ...user.company,
        modules: user.company.modules ?? null,
        logo_url: resolvedLogo || user.company.logo_url || user.company.logoUrl || ''
      }
    : user.company

  const resolvedBranchLogo = user.branch
    ? resolveCompanyLogoUrlLocal(
        user.branch.logo_url ||
        user.branch.logoUrl ||
        user.branchLogo ||
        user.branch_logo
      )
    : ''

  const branch = user.branch
    ? {
        id: user.branch.id,
        name: user.branch.name || '',
        modules: user.branch.modules ?? null,
        companyId: user.branch.companyId ?? user.branch.company_id ?? null,
        logo_url: resolvedBranchLogo || user.branch.logo_url || user.branch.logoUrl || ''
      }
    : user.branch

  return {
    ...user,
    company,
    branch,
    companyLogo: resolvedLogo || '',
    company_logo: resolvedLogo || '',
    logo: resolvedLogo || '',
    branchLogo: resolvedBranchLogo || '',
    branch_logo: resolvedBranchLogo || ''
  }
}

export default createStore({
  state: {
    token: null,
    user: null,
    companyRefreshTrigger: 0,
    selectedCompany: null,
    loginSource: null,
  },
  getters: {
    isAuthenticated(state) {
      return !!state.token
    },
    currentUser(state) {
      return state.user
    },
    refreshTrigger(state) {
      return state.companyRefreshTrigger
    },
    selectedCompany(state) {
      return state.selectedCompany
    },
    loginSource(state) {
      return state.loginSource
    }
  },
  mutations: {
    triggerRefresh(state) {
      state.companyRefreshTrigger += 1
    },
    setSelectedCompany(state, company) {
      state.selectedCompany = company
    },
    setAuth(state, { token, user, source }) {
      state.token = token
      state.user = normalizeUserLogo(user)
      state.loginSource = source || state.loginSource
    },
    normalizePersistedAuth(state) {
      if (state.user) {
        state.user = normalizeUserLogo(state.user)
      }
    },
    clearAuth(state) {
      state.token = null
      state.user = null
      state.loginSource = null
    },
  },
  actions: {
    // called by loginPage or any authentication UI
    async login({ commit }, { username, password, source }) {
      // use apiFetch directly to hit the backend login endpoint
      const response = await import('../utils/api.js').then(m => m.apiFetch('users/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }));

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const err = new Error(error.message || 'Login failed');
        if (error.code) err.code = error.code;
        if (Number.isInteger(error.remainingAttempts)) {
          err.remainingAttempts = error.remainingAttempts;
        }
        throw err;
      }

      const data = await response.json();
      commit('setAuth', { token: data.token, user: data.user, source });
      return data;
    },

    async refreshToken({ commit, state }) {
      if (!state.token) return false;

      if (refreshTokenInFlight) {
        return refreshTokenInFlight;
      }

      refreshTokenInFlight = (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/users/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${state.token}`
            }
          });

          if (!response.ok) {
            throw new Error('Token refresh failed');
          }

          const data = await response.json();
          commit('setAuth', { token: data.token, user: data.user });
          return true;
        } catch (error) {
          console.error('Token refresh failed:', error);
          return false;
        } finally {
          refreshTokenInFlight = null;
        }
      })();

      return refreshTokenInFlight;
    },

    loginSuccess({ commit }, { token, user }) {
      commit('setAuth', { token, user })
    },
    logout({ commit }) {
      commit('clearAuth')
      try {
        localStorage.removeItem('Mina_last_activity')
      } catch (e) {
        // ignore storage errors
      }
    },
  },
  modules: {
  },
  plugins: [
    createPersistedState({
      key: 'Mina_auth',
      paths: ['token', 'user', 'loginSource'],
      storage: window.localStorage,
      rehydrated: (store) => {
        store.commit('normalizePersistedAuth')
      }
    }),
  ],
})
